import type { PublishedLocaleManifest } from '@/i18n/types';

export const LOCALE_STORAGE_KEY = 'stork.locale';

let publishedLocaleManifestPromise: Promise<PublishedLocaleManifest> | null = null;

export async function loadPublishedLocaleManifest(): Promise<PublishedLocaleManifest> {
    if (!publishedLocaleManifestPromise) {
        publishedLocaleManifestPromise = fetch('/locales/manifest.json', {
            cache: 'no-store',
        })
            .then(async response => {
                if (!response.ok) {
                    throw new Error(`Failed to load locale manifest: ${response.status}`);
                }

                const manifest = parsePublishedLocaleManifest(await response.json());
                await validatePublishedStaticBundle(manifest);
                return manifest;
            })
            .catch(error => {
                publishedLocaleManifestPromise = null;
                throw error;
            });
    }

    return publishedLocaleManifestPromise;
}

export function parsePublishedLocaleManifest(value: unknown): PublishedLocaleManifest {
    if (!value || typeof value !== 'object') {
        throw new Error('Published locale manifest must be an object');
    }

    const candidate = value as Record<string, unknown>;
    const schemaVersion = readRequiredString(candidate.schemaVersion, 'schemaVersion');
    const snapshotVersion = readRequiredString(candidate.snapshotVersion, 'snapshotVersion');
    const defaultLocale = readRequiredString(candidate.defaultLocale, 'defaultLocale').toLowerCase();
    const rawLocales = candidate.locales;

    if (!Array.isArray(rawLocales) || rawLocales.length === 0) {
        throw new Error('Published locale manifest must include locales');
    }

    const locales = rawLocales.map((rawLocale, index) => parseLocale(rawLocale, index));
    const localeCodes = new Set<string>();

    for (const locale of locales) {
        if (localeCodes.has(locale.code)) {
            throw new Error(`Published locale manifest contains duplicate locale code: ${locale.code}`);
        }

        localeCodes.add(locale.code);
    }

    if (!localeCodes.has(defaultLocale)) {
        throw new Error(`Published locale manifest defaultLocale must exist in locales: ${defaultLocale}`);
    }

    const preferredHosts = parsePreferredHosts(candidate.preferredHosts, localeCodes);
    const namespaces = parseNamespaces(candidate.namespaces);
    const editorialPages = parseEditorialPages(candidate.editorialPages);

    return {
        schemaVersion,
        snapshotVersion,
        defaultLocale,
        locales,
        preferredHosts,
        namespaces,
        editorialPages,
    };
}

export function resetPublishedLocaleManifestCache(): void {
    publishedLocaleManifestPromise = null;
}

function readRequiredString(value: unknown, fieldName: string): string {
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new Error(`Published locale manifest requires ${fieldName}`);
    }

    return value.trim();
}

function parseLocale(value: unknown, index: number): PublishedLocaleManifest['locales'][number] {
    if (!value || typeof value !== 'object') {
        throw new Error(`Published locale manifest locale at index ${index} must be an object`);
    }

    const candidate = value as Record<string, unknown>;
    const code = readRequiredString(candidate.code, `locale code at index ${index}`).toLowerCase();
    const label = readRequiredString(candidate.label, `locale label at index ${index}`);

    return { code, label };
}

function parsePreferredHosts(value: unknown, localeCodes: Set<string>): Record<string, string> {
    if (value == null) {
        return {};
    }

    if (typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Published locale manifest preferredHosts must be an object');
    }

    const preferredHosts = Object.entries(value as Record<string, unknown>).reduce<Record<string, string>>((hosts, [host, locale]) => {
        const localeCode = readRequiredString(locale, `preferredHosts.${host}`).toLowerCase();

        if (!localeCodes.has(localeCode)) {
            throw new Error(`Published locale manifest preferredHosts.${host} references unknown locale: ${localeCode}`);
        }

        hosts[host.toLowerCase()] = localeCode;
        return hosts;
    }, {});

    return preferredHosts;
}

function parseNamespaces(value: unknown): string[] {
    if (!Array.isArray(value) || value.length === 0) {
        throw new Error('Published locale manifest must include namespaces');
    }

    return value.map((namespace, index) => readRequiredString(namespace, `namespace at index ${index}`));
}

function parseEditorialPages(value: unknown): Record<string, string[]> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Published locale manifest must include editorialPages');
    }

    const editorialPages = Object.entries(value as Record<string, unknown>).reduce<Record<string, string[]>>(
        (pages, [pageSlug, sections]) => {
            if (!Array.isArray(sections) || sections.length === 0) {
                throw new Error(`Published locale manifest editorialPages.${pageSlug} must include page-content sections`);
            }

            pages[pageSlug] = sections.map((sectionKey, index) =>
                readRequiredString(sectionKey, `editorialPages.${pageSlug}[${index}]`),
            );
            return pages;
        },
        {},
    );

    if (Object.keys(editorialPages).length === 0) {
        throw new Error('Published locale manifest must include editorialPages');
    }

    return editorialPages;
}

async function validatePublishedStaticBundle(manifest: PublishedLocaleManifest): Promise<void> {
    await Promise.all([
        ...manifest.namespaces.map(namespace =>
            requirePublishedAsset(`/locales/${manifest.defaultLocale}/${namespace}.json`, 'locale namespace file'),
        ),
        ...Object.entries(manifest.editorialPages).flatMap(([pageSlug, sectionKeys]) =>
            sectionKeys.map(sectionKey =>
                requirePublishedAsset(
                    `/page-content/${manifest.defaultLocale}/${pageSlug}/${sectionKey}.json`,
                    'page-content file',
                ),
            ),
        ),
    ]);
}

async function requirePublishedAsset(path: string, label: string): Promise<void> {
    const response = await fetch(path);

    if (!response.ok) {
        throw new Error(`Missing published ${label}: ${path}`);
    }
}
